import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, ShoppingBag, Users, Baby } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const highlights = [
  { icon: ShoppingBag, label: 'Fresh Produce' },
  { icon: Baby,        label: 'Diapers (Babies & Adults)' },
  { icon: Users,       label: 'Bread & Pastries' },
];

const FaithUCCMobileMarket = () => {
  const navigate = useNavigate();

  return (
    <>
      <Header />
      <div id="main-content" role="main" tabIndex={-1} className="min-h-screen bg-slate-50 pt-24">

        {/* Hero */}
        <section className="relative bg-gradient-to-br from-[#0A2037] via-[#0d2a45] to-[#079897] overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-[420px] h-[420px] bg-amber-400/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-1/4 w-[380px] h-[380px] bg-teal-400/10 rounded-full blur-3xl" />
          </div>
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm mb-6"
              data-testid="faith-ucc-back-home"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </button>

            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-400/10 border border-amber-400/40 mb-5">
              <ShoppingBag className="w-3.5 h-3.5 text-amber-300" />
              <span className="text-amber-200 text-[11px] font-bold tracking-[0.2em] uppercase">Community Partner</span>
            </div>

            <h1
              className="text-white font-bold leading-tight"
              style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(40px, 6vw, 60px)' }}
            >
              Faith UCC<br /><span className="text-teal-300">Mobile Market</span>
            </h1>

            <p className="mt-4 text-slate-200 text-base sm:text-lg leading-relaxed max-w-2xl">
              Free fresh produce, bread, pastries, and diapers for babies and adults &mdash; every 3rd Thursday of the month. Bring your bags and invite family, friends, and neighbors.
            </p>
          </div>
        </section>

        {/* Details */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Info card */}
            <div className="bg-white rounded-2xl shadow-md border border-slate-100 p-6 sm:p-7">
              <h2 className="text-xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Event Details
              </h2>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center shrink-0">
                    <Calendar className="w-4 h-4 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">When</p>
                    <p className="text-slate-800 font-semibold">3rd Thursday of every month</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center shrink-0">
                    <MapPin className="w-4 h-4 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Where</p>
                    <p className="text-slate-800 font-semibold">4900 10th St NE</p>
                    <p className="text-slate-600 text-sm">Washington, DC 20017</p>
                    <a
                      href="https://maps.google.com/?q=4900+10th+St+NE+Washington+DC+20017"
                      target="_blank"
                      rel="noreferrer"
                      className="text-teal-600 text-xs font-semibold hover:underline mt-1 inline-block"
                    >
                      Get directions &rarr;
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* What's Available card */}
            <div className="bg-gradient-to-br from-teal-50 to-amber-50 rounded-2xl border border-teal-100 p-6 sm:p-7">
              <h2 className="text-xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                What&apos;s Available
              </h2>
              <ul className="space-y-3">
                {highlights.map(({ icon: Icon, label }) => (
                  <li key={label} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-teal-700" />
                    </div>
                    <span className="text-slate-800 font-medium">{label}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-5 text-slate-600 text-sm leading-relaxed">
                All items are <strong className="text-slate-800">free</strong>. No registration or ID required. Bring bags if you have them, and don&apos;t hesitate to invite others in your community.
              </p>
            </div>
          </div>

          {/* Callout */}
          <div className="mt-8 rounded-2xl bg-[#0A2037] text-white p-6 sm:p-8 border-l-4 border-amber-400">
            <p className="text-teal-200 text-[11px] font-bold tracking-[0.2em] uppercase mb-2">Bring a Neighbor</p>
            <p className="text-lg leading-relaxed">
              The Mobile Market thrives on community. Share this with a family that could use fresh food or diapers this month &mdash; access to nutrition is part of what makes DC&apos;s youngest learners thrive.
            </p>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:justify-center">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="border-slate-300 text-slate-700 hover:bg-slate-100"
              data-testid="faith-ucc-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to DCELC
            </Button>
          </div>
        </section>

      </div>
      <Footer />
    </>
  );
};

export default FaithUCCMobileMarket;
